/**
 * PlanSwitcher — the plan's name in the chat header (and, on desktop, at the
 * top of the plan sidebar), opening the menu for everything above the plan:
 * the other plans on this account, a new plan, and Account settings. There is
 * no plans list screen to go back to; this menu replaces it.
 *
 * Desktop opens a popover under the name; phones a bottom sheet.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSelector } from 'react-redux';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useTheme } from '../hooks/useTheme';
import { useAccountLimits } from '../hooks/useAccountLimits';
import { useRoutes } from '../navigation/useRoutes';
import { navigationRef } from '../navigation/navigationRef';
import {
  lockedPlanIds,
  openAccountSettings,
  openPlanChat,
  useOpenLockedPlan,
  useOpenNewPlan,
} from '../navigation/planSwitching';
import { fetchIntakePending, type IntakePendingRow } from '../store/actions/projects';
import { intakeDraftName } from './IntakePendingCard';
import { projectLabel } from '../utilities/projectLabel';
import { isWeb, DESKTOP_BREAKPOINT } from '../utilities/platform';
import Icon from './Icon';
import UserAvatar from './UserAvatar';
import type { RootState } from '../store';

// Past this many plans the menu gets a search box: an agency's caseload is
// the case the plans list's search used to cover.
const SEARCH_AFTER = 6;
const POPOVER_WIDTH = 300;

interface Props {
  /** The plan on screen; null while it loads. */
  project: any | null;
  /** Trigger text. Defaults to the plan's label. */
  title?: string;
  subtitle?: string | null;
  variant?: 'header' | 'sidebar';
  /** DOM id on web (guided-tour anchor). */
  anchorId?: string;
}

type Anchor = { x: number; y: number; width: number; height: number };

/** A plan as the person viewing it names it: their own is "Your plan". */
export function planMenuLabel(p: any, viewerCognitoId?: string | null): string {
  if (p?.recipient_cognito_id && viewerCognitoId && p.recipient_cognito_id === viewerCognitoId) return 'Your plan';
  return projectLabel(p);
}

export default function PlanSwitcher({ project, title, subtitle, variant = 'header', anchorId }: Props) {
  const theme = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { width: windowWidth } = useWindowDimensions();
  const isDesktop = isWeb && windowWidth >= DESKTOP_BREAKPOINT;
  const triggerRef = useRef<View>(null);
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const [search, setSearch] = useState('');
  const [drafts, setDrafts] = useState<IntakePendingRow[]>([]);

  const projectsMap = useSelector((s: RootState) => s.projects.list);
  const viewerCognitoId = useSelector((s: RootState) => s.authentication?.user?.cognito_id ?? null);
  const systemName: string = useSelector(
    (s: RootState) => (s.session?.settings as any)?.system_name || (s.session?.account as any)?.system_name || 'Ellie',
  );
  const { tabs } = useRoutes();
  const { maxProjects } = useAccountLimits();
  const openNewPlan = useOpenNewPlan();
  const openLockedPlan = useOpenLockedPlan();

  const plans = useMemo(() => Object.values(projectsMap || {}) as any[], [projectsMap]);
  const locked = useMemo(() => lockedPlanIds(plans, maxProjects), [plans, maxProjects]);
  const currentId = project?.id ?? project?.project_id ?? null;
  const visiblePlans = useMemo(() => {
    const q = search.trim().toLowerCase();
    return plans
      .filter((p) => !q || planMenuLabel(p, viewerCognitoId).toLowerCase().includes(q))
      .sort((a, b) => {
        const lockedA = locked.has(String(a.id));
        const lockedB = locked.has(String(b.id));
        if (lockedA !== lockedB) return lockedA ? 1 : -1;
        return planMenuLabel(a, viewerCognitoId).localeCompare(planMenuLabel(b, viewerCognitoId));
      });
  }, [plans, search, locked, viewerCognitoId]);
  const hasAccountHome = tabs.some((t) => t.name === 'Home') && plans.length > 1;
  const hasAccountChat = tabs.some((t) => t.name === 'AI');

  const close = useCallback(() => {
    setOpen(false);
    setSearch('');
  }, []);

  const show = useCallback(() => {
    // Drafts are the plans being set up; the plans list used to surface them.
    fetchIntakePending().then((res) => { if (res.success) setDrafts(res.rows); }).catch(() => {});
    if (!isDesktop || !triggerRef.current?.measureInWindow) {
      setOpen(true);
      return;
    }
    triggerRef.current.measureInWindow((x, y, width, height) => {
      setAnchor({ x, y, width, height });
      setOpen(true);
    });
  }, [isDesktop]);

  const run = useCallback((action: () => void) => {
    close();
    action();
  }, [close]);

  const goTo = (screen: 'Home' | 'AI') => {
    if (navigationRef.isReady()) (navigationRef as any).navigate('Main', { screen });
  };

  const label = title || (project ? planMenuLabel(project, viewerCognitoId) : '');
  const recipient = project?.users?.find((u: any) => u.type === 'recipient');
  const avatarSize = variant === 'sidebar' ? 28 : 32;

  const row = (key: string, icon: IconProp, text: string, onPress: () => void, extra?: React.ReactNode) => (
    <TouchableOpacity
      key={key}
      testID={`plan-switcher-${key}`}
      style={styles.row}
      onPress={() => run(onPress)}
      accessibilityRole={isDesktop ? 'menuitem' : 'button'}
    >
      <View style={styles.rowIcon}><Icon icon={icon} size={15} color={theme.colors.muted} /></View>
      <Text style={[styles.rowText, { color: theme.colors.text }]} numberOfLines={1}>{text}</Text>
      {extra}
    </TouchableOpacity>
  );
  const divider = (key: string) => <View key={key} style={[styles.divider, { backgroundColor: theme.colors.border }]} />;

  const menu = (
    <>
      {plans.length > SEARCH_AFTER && (
        <View style={[styles.searchWrap, { borderColor: theme.colors.border, backgroundColor: theme.colors.surface }]}>
          <Icon icon={['fas', 'magnifying-glass']} size={12} color={theme.colors.muted} />
          <TextInput
            value={search}
            onChangeText={setSearch}
            placeholder={`Search ${plans.length} plans`}
            placeholderTextColor={theme.colors.muted}
            style={[styles.searchInput, { color: theme.colors.text }]}
            accessibilityLabel="Search plans"
            autoFocus={isDesktop}
          />
        </View>
      )}
      {!search && hasAccountHome && row('all-plans', ['fad', 'grid-2'], 'All plans', () => goTo('Home'))}
      {!search && hasAccountChat && row('account-chat', ['fas', 'sparkles'], `Ask ${systemName} across plans`, () => goTo('AI'))}
      {!search && (hasAccountHome || hasAccountChat) && divider('plans-divider')}
      {visiblePlans.map((p) => {
        const isLocked = locked.has(String(p.id));
        const isCurrent = String(p.id) === String(currentId);
        const pRecipient = p.users?.find((u: any) => u.type === 'recipient');
        return (
          <TouchableOpacity
            key={`plan-${p.id}`}
            testID={`plan-switcher-plan-${p.id}`}
            style={[styles.row, isCurrent && { backgroundColor: theme.colors.surface }]}
            onPress={() => run(() => {
              if (isLocked) openLockedPlan();
              else if (!isCurrent) openPlanChat(p);
            })}
            accessibilityRole={isDesktop ? 'menuitem' : 'button'}
            accessibilityState={{ selected: isCurrent }}
            aria-selected={isCurrent}
            accessibilityLabel={isLocked ? `${planMenuLabel(p, viewerCognitoId)}, locked` : planMenuLabel(p, viewerCognitoId)}
          >
            <UserAvatar
              cognitoId={p.recipient_cognito_id}
              firstName={p.first_name}
              lastName={p.last_name}
              hasAvatar={!!pRecipient?.avatar}
              updatedAt={pRecipient?.updated_at}
              size={24}
            />
            <Text style={[styles.rowText, { color: isLocked ? theme.colors.muted : theme.colors.text }]} numberOfLines={1}>
              {planMenuLabel(p, viewerCognitoId)}
            </Text>
            {isLocked ? <Icon icon={['fas', 'lock']} size={12} color={theme.colors.muted} />
              : isCurrent ? <Icon icon={['fas', 'check']} size={13} color={theme.colors.primary} /> : null}
          </TouchableOpacity>
        );
      })}
      {search && visiblePlans.length === 0 && (
        <Text style={[styles.empty, { color: theme.colors.muted }]}>No plan matches "{search.trim()}".</Text>
      )}
      {!search && drafts.map((draft) => row(
        `draft-${draft.project_id}`,
        ['fad', 'pen-to-square'],
        intakeDraftName(draft) ? `Finish setting up ${intakeDraftName(draft)}` : 'Finish setting up a plan',
        () => { if (navigationRef.isReady()) (navigationRef as any).navigate('Main', { screen: 'Plans', params: { screen: 'CreatePlan', params: { resumeIntakeId: draft.project_id } } }); },
      ))}
      {divider('new-divider')}
      <TouchableOpacity
        testID="plan-switcher-new"
        style={styles.row}
        onPress={() => run(openNewPlan)}
        accessibilityRole={isDesktop ? 'menuitem' : 'button'}
      >
        <View style={styles.rowIcon}><Icon icon={['fas', 'plus']} size={14} color={theme.colors.primary} /></View>
        <Text style={[styles.rowText, { color: theme.colors.primary }]}>New plan</Text>
      </TouchableOpacity>
      {divider('settings-divider')}
      {row('settings', ['fad', 'gear'], 'Account settings', openAccountSettings)}
    </>
  );

  const popoverLeft = anchor ? Math.max(8, Math.min(anchor.x, windowWidth - POPOVER_WIDTH - 12)) : 0;

  return (
    <>
      <TouchableOpacity
        ref={triggerRef as any}
        testID="plan-switcher"
        id={anchorId}
        style={[styles.trigger, variant === 'sidebar' && styles.triggerSidebar, open && { backgroundColor: theme.colors.surface }]}
        onPress={show}
        accessibilityRole="button"
        accessibilityLabel={label ? `${label}. Switch plan` : 'Switch plan'}
        accessibilityState={{ expanded: open }}
        activeOpacity={0.75}
      >
        {project ? (
          <UserAvatar
            cognitoId={project.recipient_cognito_id}
            firstName={project.first_name}
            lastName={project.last_name}
            hasAvatar={!!recipient?.avatar}
            updatedAt={recipient?.updated_at}
            size={avatarSize}
          />
        ) : null}
        <View style={styles.triggerText}>
          <Text
            style={[variant === 'sidebar' ? styles.sidebarTitle : styles.headerTitle, { color: theme.colors.text }]}
            numberOfLines={1}
          >
            {label}
          </Text>
          {subtitle ? (
            <Text style={[styles.subtitle, { color: theme.colors.muted }]} numberOfLines={1}>{subtitle}</Text>
          ) : null}
        </View>
        <Icon icon={['fas', 'chevron-down']} size={11} color={theme.colors.muted} />
      </TouchableOpacity>

      <Modal visible={open} transparent animationType={isDesktop ? 'fade' : 'slide'} onRequestClose={close}>
        {isDesktop ? (
          <Pressable style={StyleSheet.absoluteFill} onPress={close} accessibilityLabel="Close plan menu">
            <Pressable
              testID="plan-switcher-menu"
              accessibilityRole="menu"
              onPress={() => {}}
              style={[
                styles.popover,
                { top: (anchor?.y ?? 0) + (anchor?.height ?? 0) + 6, left: popoverLeft, backgroundColor: theme.colors.card, borderColor: theme.colors.border },
              ]}
            >
              <ScrollView style={styles.popoverScroll} keyboardShouldPersistTaps="handled">{menu}</ScrollView>
            </Pressable>
          </Pressable>
        ) : (
          <Pressable style={styles.overlay} onPress={close} accessibilityLabel="Close plan menu">
            <Pressable testID="plan-switcher-menu" onPress={() => {}} style={[styles.sheet, { backgroundColor: theme.colors.card }]}>
              <View style={[styles.handle, { backgroundColor: theme.colors.border }]} />
              <ScrollView style={styles.sheetScroll} keyboardShouldPersistTaps="handled">{menu}</ScrollView>
            </Pressable>
          </Pressable>
        )}
      </Modal>
    </>
  );
}

const makeStyles = (theme: ReturnType<typeof useTheme>) => StyleSheet.create({
  trigger: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 4,
    paddingLeft: 4,
    paddingRight: 8,
    borderRadius: 10,
  },
  triggerSidebar: { flexGrow: 0, flexShrink: 1, flexBasis: 'auto', alignSelf: 'stretch', gap: 8 },
  triggerText: { flexShrink: 1, minWidth: 0 },
  headerTitle: { fontSize: 17, fontWeight: '600' },
  sidebarTitle: { fontSize: 15, fontWeight: '600' },
  subtitle: { fontSize: 12, marginTop: 1 },
  popover: {
    position: 'absolute',
    width: POPOVER_WIDTH,
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    paddingVertical: 6,
    overflow: 'hidden',
    ...theme.shadow.elevated,
  },
  popoverScroll: { maxHeight: 440 },
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'flex-end' },
  sheet: { borderTopLeftRadius: 20, borderTopRightRadius: 20, paddingTop: 8, paddingBottom: 28 },
  sheetScroll: { maxHeight: 520 },
  handle: { width: 36, height: 4, borderRadius: 2, alignSelf: 'center', marginBottom: 8 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 9, paddingHorizontal: 14 },
  rowIcon: { width: 24, alignItems: 'center' },
  rowText: { flex: 1, fontSize: 15 },
  divider: { height: StyleSheet.hairlineWidth, marginVertical: 5, marginHorizontal: 12 },
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginHorizontal: 10,
    marginTop: 4,
    marginBottom: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
  },
  searchInput: { flex: 1, fontSize: 14, paddingVertical: 7 },
  empty: { fontSize: 13, paddingHorizontal: 14, paddingVertical: 10 },
});
